import React, { useState, useEffect, useCallback } from 'react';
import { getLeads, getOrganizationLeads, getTeamsByOrganization, checkResetTeam, setResetTeam } from '../api';
import CustomerDetail from './CustomerDetail';
import LeadForm from './forms/LeadForm';
import WpForm from './forms/WpForm';
// Entferne den Import von DashboardNav
// import DashboardNav from '../components/dashboard/DashboardNav';
import { useNavigate } from 'react-router-dom';

function Leads({ user, agenturId, currentFilter }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPvForm, setShowPvForm] = useState(false);
  const [showWpForm, setShowWpForm] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  
  // Neue States für Callcenter-Funktionalität
  const [teams, setTeams] = useState([]);
  const [allLeads, setAllLeads] = useState([]); // Ungefilterte Leads
  const [availableServiceProviders, setAvailableServiceProviders] = useState([]); // Neu hinzugefügt
  
  const [filters, setFilters] = useState(() => {
    // Filter aus localStorage laden oder Standardwerte verwenden
    const savedFilters = localStorage.getItem('leadsFilters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (error) {
        console.error('Fehler beim Laden der gespeicherten Filter:', error);
      }
    }
    // Standardwerte
    return {
      teamId: '', // Alle Teams
      sortBy: 'erstellt', // erstellt, name
      sortOrder: 'desc', // desc, asc
      dateFrom: '',
      dateTo: '',
      statusPv: '', // Alle Status
      statusWp: '', // Alle Status
    };
  });
  const navigate = useNavigate();
  // Rollenprüfungen
  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isCallcenterMitarbeiter = user?.isCallcenterMitarbeiter || false;
  const isCallcenterRole = isCallcenterLeader || isCallcenterMitarbeiter;
  const canCreateLeads = !isCallcenterLeader && !isCallcenterMitarbeiter;

  // Mobile Responsive Check
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive Styles with improved table sizing
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px', // Increased max width
      padding: isMobile ? '5px' : '15px', // Reduced padding
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px', // Reduced margin
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem', // Slightly smaller
      margin: 0,
    },
    actions: {
      display: 'flex',
      gap: '8px', // Reduced gap
      flexDirection: isMobile ? 'column' : 'row',
      width: isMobile ? '100%' : 'auto',
    },
    actionButton: {
      padding: '6px 12px', // Reduced padding
      background: '#0a9a22',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      width: isMobile ? '100%' : 'auto',
      fontSize: '13px', // Slightly smaller
    },
    filters: {
      background: 'white',
      borderRadius: '6px', // Slightly smaller radius
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '10px' : '15px', // Reduced padding
      marginBottom: '15px',
    },
    filterRow: {
      display: 'flex',
      gap: isMobile ? '8px' : '15px', // Reduced gap
      marginBottom: '12px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: isMobile ? 'auto' : '120px', // Reduced min width
      flex: isMobile ? '1' : 'none',
    },
    filterLabel: {
      fontSize: '0.8rem', // Smaller font
      fontWeight: '500',
      color: '#4a5568',
      marginBottom: '4px',
    },
    filterInput: {
      padding: '6px 8px', // Reduced padding
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem', // Smaller font
    },
    resetButton: {
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      marginTop: isMobile ? '8px' : '15px',
      width: isMobile ? '100%' : 'auto',
    },
    tableContainer: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      // Max height to prevent excessive scrolling
      maxHeight: '70vh',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '900px' : isTablet ? '1000px' : 'auto',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.85rem',
    },
    th: {
      background: '#f7fafc',
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px', // Significantly reduced padding
      textAlign: 'left',
      fontWeight: '600',
      color: '#4a5568',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    td: {
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px', // Significantly reduced padding
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      maxWidth: isMobile ? '80px' : isTablet ? '100px' : '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Specific column widths
    dateColumn: {
      minWidth: isMobile ? '110px' : '120px',
      maxWidth: isMobile ? '130px' : '150px',
    },
    productColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '60px',
    },
    nameColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '120px' : '150px',
    },
    emailColumn: {
      minWidth: isMobile ? '90px' : '120px',
      maxWidth: isMobile ? '120px' : '180px',
    },
    phoneColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    teamColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '60px' : '100px',
    },
    callsColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '80px',
    },
    statusColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    statusBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.15rem 0.3rem' : '0.2rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: isMobile ? '0.55rem' : '0.65rem',
      fontWeight: '500',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      minWidth: isMobile ? '35px' : '50px',
      maxWidth: isMobile ? '60px' : '80px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    statusNew: {
      backgroundColor: '#ebf8ff',
      color: '#3182ce',
    },
    statusCompleted: {
      backgroundColor: '#c6f6d5',
      color: '#38a169',
    },
    statusCancelled: {
      backgroundColor: '#fed7d7',
      color: '#e53e3e',
    },
    productBadges: {
      display: 'flex',
      gap: '0.15rem',
      flexWrap: 'wrap',
    },
    productBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.1rem 0.25rem' : '0.15rem 0.4rem',
      borderRadius: '0.2rem',
      fontSize: isMobile ? '0.55rem' : '0.6rem',
      fontWeight: '700',
    },
    pvBadge: {
      backgroundColor: '#c3dafe',
      color: '#4a6de5',
    },
    wpBadge: {
      backgroundColor: '#b2f5ea',
      color: '#3182ce',
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      color: '#718096',
      fontSize: '1rem',
    },
    error: {
      color: '#e53e3e',
      backgroundColor: '#fed7d7',
      padding: '8px',
      borderRadius: '4px',
      marginBottom: '12px',
      textAlign: 'center',
      fontSize: '0.85rem',
    },
    noData: {
      textAlign: 'center',
      padding: '20px',
      color: '#a0aec0',
      fontSize: '0.9rem',
    },
    mobileScrollHint: {
      display: isMobile || isTablet ? 'block' : 'none',
      fontSize: '0.65rem',
      color: '#718096',
      textAlign: 'center',
      marginTop: '8px',
      fontStyle: 'italic',
      padding: '5px',
    },
    // Neue Styles für BO-Kommentar
    boCommentColumn: {
      minWidth: isMobile ? '120px' : '150px',
      maxWidth: isMobile ? '200px' : '250px',
    },
    boCommentContainer: {
      backgroundColor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      padding: '6px 8px',
      fontSize: isMobile ? '0.65rem' : '0.75rem',
      color: '#4a5568',
      maxHeight: '60px',
      overflow: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f1f5f9'
      }
    },
  };

  // Teams laden für Callcenter-Rollen
  const loadTeams = useCallback(async () => {
    if (!isCallcenterRole || !user.organization_id) return;
    
    try {
      const data = await getTeamsByOrganization(user.id, user.organization_id);
      if (data.success) {
        setTeams(data.teams || []);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teams:', err);
    }
  }, [isCallcenterRole, user.id, user.organization_id]);

  // Leads abrufen
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      let data;
      if (isCallcenterRole) {
        // Für Callcenter: Alle Leads der Organisation
        data = await getOrganizationLeads(user.id, user.organization_id);
      } else {
        // Für normale Benutzer: Nur eigene Leads
        data = await getLeads(user.id);
      }
    
    if (data.success) {
      setAllLeads(data.leads);
    } else {
        setError(data.error || 'Fehler beim Abrufen der Leads');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user.id, user.organization_id, isCallcenterRole]);

  // Erweiterte Filter-Logik mit Navigation-Filtern
  const applyFilters = useCallback(() => {
    let filtered = [...allLeads];
    
    // Navigation-Filter anwenden (nur für Callcenter-Rollen)
    if (isCallcenterRole && currentFilter) {
      // Test Leads Filter zuerst prüfen und anwenden
      if (currentFilter === 'test-leads') {
        filtered = filtered.filter(lead => 
          lead.status_pv === 'Test Lead' || lead.status_wp === 'Test Lead'
        );
        setLeads(filtered);
        return; // Keine weiteren Filter anwenden
      }

      // Andere Filter nur anwenden, wenn es keine Test Leads sind
      switch (currentFilter) {
        case 'new':
          filtered = filtered.filter(lead => 
            (!lead.has_calls || lead.call_count === 0) && 
            lead.status_pv !== 'Test Lead' && 
            lead.status_wp !== 'Test Lead'
          );
          break;
        case 'open':
          filtered = filtered.filter(lead => 
            (lead.signature || lead.opt_in) && 
            (!lead.has_calls || lead.call_count === 0) && 
            lead.status_pv !== 'Test Lead' && 
            lead.status_wp !== 'Test Lead' && 
            lead.customer_status !== 'Termin Storniert'
          );
          break;
        case 'ohne-opt-in':
          filtered = filtered.filter(lead => 
            !lead.signature && !lead.opt_in && 
            lead.status_pv !== 'Test Lead' && 
            lead.status_wp !== 'Test Lead'
          );
          break;
        case 'on-hold':
          filtered = filtered.filter(lead => 
            lead.customer_status === 'On Hold' && 
            lead.status_pv !== 'Test Lead' && 
            lead.status_wp !== 'Test Lead'
          );
          break;
        case 'termin-abgesagt':
          filtered = filtered.filter(lead => 
            lead.customer_status === 'Termin Storniert' && 
            lead.status_pv !== 'Test Lead' && 
            lead.status_wp !== 'Test Lead'
          );
          break;
        case 'ne-kunden':
          filtered = filtered.filter(lead => {
            let count = 0;
            if (Array.isArray(lead.calls)) {
              count = lead.calls.filter(call => call.status === 'Nicht erreicht').length;
            }
            // Wenn kein calls-Array vorhanden, NICHT anzeigen!
            if (!Array.isArray(lead.calls)) return false;
            return (lead.last_call_status === 'Nicht erreicht' || lead.last_call_status === 'Abgelehnt') && count < 15;
          });
          break;
        case 'spaeter-anrufen':
          filtered = filtered.filter(lead => 
            (lead.last_call_status === 'Rückruf erwünscht' || lead.last_call_status === 'Später anrufen') && 
            lead.status_pv !== 'Test Lead' && 
            lead.status_wp !== 'Test Lead'
          );
          break;
        case 'final-nicht-erreicht':
          if (isCallcenterRole) {
            filtered = filtered.filter(lead => {
              let count = 0;
              if (Array.isArray(lead.calls)) {
                count = lead.calls.filter(call => call.status === 'Nicht erreicht').length;
              }
              if (!Array.isArray(lead.calls)) return false;
              return count >= 14;
            });
          }
          break;
        case 'final-abgesagt':
          if (isCallcenterRole) {
            filtered = filtered.filter(lead => 
              lead.customer_status === 'Final abgesagt' &&
              lead.status_pv !== 'Test Lead' && 
              lead.status_wp !== 'Test Lead'
            );
          }
          break;
        default:
          // Kein spezieller Filter
          break;
      }
    }

    // Filter für alle Benutzer (Team, Datum, Status) anwenden
    // Team-Filter (relevant für Callcenter-Leader, wenn kein Nav-Filter aktiv ist)
    if (filters.teamId) {
      filtered = filtered.filter(lead => lead.team_id?.toString() === filters.teamId.toString());
    }

    // Datums-Filter
    if (filters.dateFrom) {
      filtered = filtered.filter(lead => new Date(lead.erstellt) >= new Date(filters.dateFrom));
    }
    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setHours(23, 59, 59, 999); // Ende des Tages berücksichtigen
      filtered = filtered.filter(lead => new Date(lead.erstellt) <= dateTo);
    }
    
    // Status-Filter
    if (filters.statusPv) {
      filtered = filtered.filter(lead => lead.status_pv === filters.statusPv);
    }
    if (filters.statusWp) {
      filtered = filtered.filter(lead => lead.status_wp === filters.statusWp);
    }

    // Sortierung anwenden
    if (filters.sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    } else { // Standard-Sortierung nach Erstelldatum
      filtered.sort((a, b) => new Date(b.erstellt) - new Date(a.erstellt));
    }

    if (filters.sortOrder === 'asc') {
      filtered.reverse();
    }

    setLeads(filtered);
  }, [allLeads, filters, currentFilter, isCallcenterRole]);

  // useEffects
  useEffect(() => {
    fetchLeads();
    if (isCallcenterRole) {
      loadTeams();
    }
  }, [fetchLeads, loadTeams]);

  // Automatisches Neuladen der Leads mit Page Visibility (nur für normale Benutzer)
  useEffect(() => {
    // Normale Benutzer: 2.5 Minuten (150 Sekunden) - schont Akku
    const pollingInterval = 150000;
    let intervalId;

    const startPolling = () => {
      intervalId = setInterval(() => {
        fetchLeads();
        
        // Nur in Entwicklung loggen
        if (process.env.NODE_ENV === 'development') {
          console.log('Leads neu geladen:', new Date().toLocaleString('de-DE'), {
            anzahlLeads: allLeads.length,
            intervall: '2.5 Minuten'
          });
        }
      }, pollingInterval);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    // Polling pausieren wenn Tab nicht sichtbar (spart Akku)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
        if (process.env.NODE_ENV === 'development') {
          console.log('Leads Polling pausiert - Tab nicht sichtbar');
        }
      } else {
        fetchLeads(); // Sofort aktualisieren beim Zurückkommen
        startPolling();
        if (process.env.NODE_ENV === 'development') {
          console.log('Leads Polling fortgesetzt - Tab wieder sichtbar');
        }
      }
    };

    // Initial starten
    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup: Interval und Event Listener entfernen
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchLeads, allLeads]);

  // Filter anwenden wenn sich allLeads, filters oder currentFilter ändern
  useEffect(() => {
    applyFilters(); // Für alle Benutzer anwenden
  }, [allLeads, filters, applyFilters, currentFilter]);

  // Filter im localStorage speichern, wenn sie sich ändern
  useEffect(() => {
    localStorage.setItem('leadsFilters', JSON.stringify(filters));
  }, [filters]);

  // Status-Farben-Funktion
  const getStatusColor = (status) => {
    if (!status) return styles.statusNew;
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return styles.statusNew;
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return { backgroundColor: '#fefcbf', color: '#d69e2e' };
    if (status.includes('abgeschlossen') || status.includes('fertig')) return styles.statusCompleted;
    if (status.includes('qualifiziert')) return styles.statusCompleted;
    if (status.includes('abgelehnt') || status.includes('storniert') || status.includes('kein interesse') || status.includes('widerruf') || status.includes('ungültig') || status.includes('test')) return styles.statusCancelled;
    return styles.statusNew;
  };

  // Status-Rendering mit Validierung
  const renderStatus = (status, productType, leadProduct, invalidFlag) => {
    const hasProduct = leadProduct && leadProduct.toLowerCase().includes(productType.toLowerCase());
    
    if (!hasProduct) {
      return <span>-</span>;
    }
    
    if (invalidFlag) {
      return (
        <span style={{...styles.statusBadge, ...styles.statusCancelled}} title="Kein Lead">
          {isMobile ? 'N/A' : 'Kein Lead'}
        </span>
      );
    }
    
    if (!status || status.trim() === '') {
      return (
        <span style={{...styles.statusBadge, ...styles.statusNew}}>
          Offen
        </span>
      );
    }
    
    const statusText = isMobile && status.length > 6 ? status.substring(0, 6) + '..' : 
                      isTablet && status.length > 8 ? status.substring(0, 8) + '..' : status;
    
    return (
      <span style={{...styles.statusBadge, ...getStatusColor(status)}} title={status}>
        {statusText}
      </span>
    );
  };

  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Filter zurücksetzen (aktualisiert)
  const resetFilters = () => {
    const defaultFilters = {
      teamId: '',
      sortBy: 'erstellt',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      statusPv: '',
      statusWp: '',
    };
    setFilters(defaultFilters);
    // Auch aus localStorage entfernen
    localStorage.removeItem('leadsFilters');
  };

  // Kunde auswählen
  const handleCustomerSelect = (customerId) => {
    navigate(`/customer/${customerId}`);
  };

  // Zurück zur Liste
  const handleBackToList = () => {
    setSelectedCustomerId(null);
  };

  // Team-Name finden
  const getTeamName = (teamId) => {
    if (!teamId) return '-';
    const team = teams.find(t => t.id.toString() === teamId.toString());
    if (!team) return 'Unbekannt';
    
    // Shortened team names for mobile/tablet
    if (isMobile && team.name.length > 6) {
      return team.name.substring(0, 6) + '..';
    }
    if (isTablet && team.name.length > 10) {
      return team.name.substring(0, 10) + '..';
    }
    return team.name;
  };

  // Filter-Name für die Anzeige
  const getFilterDisplayName = (filter) => {
    switch (filter) {
      case 'new': return 'Neue Leads';
      case 'open': return 'Offene Leads (mit Opt-In)';
      case 'ohne-opt-in': return 'Ohne Opt-In';
      case 'qualified': return 'Qualifizierte Leads';
      case 'not-reached': return 'Nicht erreichte Leads';
      case 'not-interested': return 'Nicht interessierte Leads';
      case 'not-qualified': return 'Nicht qualifizierte Leads';
      case 'all': return 'Alle Leads';
      case 'test-leads': return 'Test Leads';
      default: return 'Alle Leads';
    }
  };

  // Truncate text helper
  const truncateText = (text, maxLength) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '..';
  };

  const formatCallbackTime = (ruckrufZeit) => {
    if (!ruckrufZeit) return '-';
    
    try {
      const date = new Date(ruckrufZeit);
      if (isNaN(date.getTime())) return '-';
      
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Fehler beim Formatieren der Rückruf-Zeit:', error);
      return '-';
    }
  };

  // Hilfsfunktion für ResetTeam-Check und ggf. Logout
  const handleLeadButtonClick = async (type) => {
    if (!user?.id) return;
    // Prüfe ResetTeam-Status
    const res = await checkResetTeam(user.id);
    if (res.success && typeof res.resetTeam !== 'undefined') {
      if (res.resetTeam === 0) {
        // Setze ResetTeam auf 1
        await setResetTeam(user.id);
        // Session/Token löschen
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('sessionData');
        // Weiterleitung zur Login-Seite
        window.location.href = '/login';
        return;
      } else if (res.resetTeam === 1) {
        // Weiterleitung zum jeweiligen Lead-Formular
        if (type === 'PV') {
          navigate('/add-pv-lead');
        } else if (type === 'WP') {
          navigate('/add-wp-lead');
        } else if (type === 'PV+WP') {
          navigate('/add-pv-wp-lead');
        }
        return;
      }
    } else {
      alert('Fehler beim Prüfen des ResetTeam-Status!');
    }
  };

  // Handler für Navigation
  const handleLogout = () => {
    navigate('/');
  };
  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      navigate('/dashboard');
    } else if (section === 'agentur-dashboard') {
      navigate(`/agentur-dashboard/${agenturId}`);
    } else if (section === 'mitglieder') {
      navigate(`/agenturen/${agenturId}/mitglieder`);
    } else if (section === 'leads') {
      navigate(`/agenturen/${agenturId}/leads`);
    }
  };

  // Wenn ein Kunde ausgewählt ist, zeige die Detailansicht
  if (selectedCustomerId) {
    return (
      <CustomerDetail 
        user={user} 
        customerId={selectedCustomerId} 
        onBack={() => setSelectedCustomerId(null)} 
      />
    );
  }

  const showCallbackTimeColumn = isCallcenterRole && currentFilter === 'spaeter-anrufen'; 

  return (
    <>
      <div style={styles.container}>
        {showPvForm ? (
          <LeadForm
            user={user}
            onCancel={() => setShowPvForm(false)}
            onLeadAdded={fetchLeads}
          />
        ) : showWpForm ? (
          <WpForm
            user={user}
            onCancel={() => setShowWpForm(false)}
            onLeadAdded={fetchLeads}
          />
        ) : (
          <>
            <div style={styles.header}>
              <h2 style={styles.title}>
                {isCallcenterRole ? 
                  (currentFilter ? getFilterDisplayName(currentFilter) : 'Alle Organisations-Kunden') : 
                  'Kunden'
                }
                {currentFilter && (
                  <span style={{ 
                    fontSize: '0.7em', 
                    color: '#4a6de5', 
                    marginLeft: '8px',
                    fontWeight: 'normal'
                  }}>
                    ({leads.length})
                  </span>
                )}
              </h2>
              {canCreateLeads && (
                <div style={styles.actions}>
                  <button style={styles.actionButton} onClick={() => handleLeadButtonClick('PV')}>
                    + PV Lead
                  </button>
                  <button style={styles.actionButton} onClick={() => handleLeadButtonClick('WP')}>
                    + WP Lead
                  </button>
                  <button style={styles.actionButton} onClick={() => handleLeadButtonClick('PV+WP')}>
                    + PV & WP Lead
                  </button>
                </div>
              )}
            </div>

            {!currentFilter && (
              <div style={styles.filters}>
                <div style={styles.filterRow}>
                  {/* Team-Filter nur für Callcenter-Rollen */}
                  {isCallcenterRole && (
                    <div style={styles.filterGroup}>
                      <label style={styles.filterLabel}>Projekt:</label>
                      <select 
                        style={styles.filterInput}
                        value={filters.teamId} 
                        onChange={(e) => handleFilterChange('teamId', e.target.value)}
                      >
                        <option value="">Alle Teams</option>
                        {teams.map(team => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Sortieren:</label>
                    <select 
                      style={styles.filterInput}
                      value={filters.sortBy} 
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    >
                      <option value="erstellt">Datum</option>
                      <option value="name">Name</option>
                    </select>
                  </div>
                  
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Reihenfolge:</label>
                    <select 
                      style={styles.filterInput}
                      value={filters.sortOrder} 
                      onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    >
                      <option value="desc">↓ Neu-Alt</option>
                      <option value="asc">↑ Alt-Neu</option>
                    </select>
                  </div>
                </div>
                
                <div style={styles.filterRow}>
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Von:</label>
                    <input 
                      style={styles.filterInput}
                      type="date" 
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                  </div>
                  
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>Bis:</label>
                    <input 
                      style={styles.filterInput}
                      type="date" 
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </div>
                  
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>PV Status:</label>
                    <select 
                      style={styles.filterInput}
                      value={filters.statusPv} 
                      onChange={(e) => handleFilterChange('statusPv', e.target.value)}
                    >
                      <option value="">Alle</option>
                      <option value="Offen">Offen</option>
                      <option value="Qualifiziert">Qualifiziert</option>
                      <option value="Kein Interesse mehr">Kein Interesse</option>
                      <option value="Widerruf">Widerruf</option>
                      <option value="Telefonnummer ungültig">Ungültig</option>
                      <option value="Test Lead">Test Lead</option>
                    </select>
                  </div>
                  
                  <div style={styles.filterGroup}>
                    <label style={styles.filterLabel}>WP Status:</label>
                    <select 
                      style={styles.filterInput}
                      value={filters.statusWp} 
                      onChange={(e) => handleFilterChange('statusWp', e.target.value)}
                    >
                      <option value="">Alle</option>
                      <option value="Offen">Offen</option>
                      <option value="Qualifiziert">Qualifiziert</option>
                      <option value="Kein Interesse mehr">Kein Interesse</option>
                      <option value="Widerruf">Widerruf</option>
                      <option value="Telefonnummer ungültig">Ungültig</option>
                      <option value="Test Lead">Test Lead</option>
                    </select>
                  </div>
                </div>

                <button style={styles.resetButton} onClick={resetFilters}>
                  Filter zurücksetzen
                </button>
              </div>
            )}
          
            {error && <div style={styles.error}>{error}</div>}
            {loading ? (
              <div style={styles.loading}>Leads werden geladen...</div>
            ) : leads.length === 0 ? (
              <div style={styles.noData}>
                {currentFilter ? 
                  `Keine ${getFilterDisplayName(currentFilter).toLowerCase()} gefunden.` : 
                  'Keine Leads gefunden.'
                }
              </div>
            ) : (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{...styles.th, ...styles.dateColumn}}>Erstellt</th>
                      <th style={{...styles.th, ...styles.productColumn}}>Produkt</th>
                      <th style={{...styles.th, ...styles.statusColumn}}>Opt In</th>
                      <th style={{...styles.th, ...styles.nameColumn}}>Name</th>
                      <th style={{...styles.th, ...styles.emailColumn}}>E-Mail</th>
                      <th style={{...styles.th, ...styles.phoneColumn}}>Handy</th>
                      {isCallcenterRole && <th style={{...styles.th, ...styles.teamColumn}}>Team</th>}
                      {isCallcenterRole && <th style={{...styles.th, ...styles.callsColumn}}>Anrufe</th>}
                      {isCallcenterRole && showCallbackTimeColumn && <th style={{...styles.th, ...styles.callsColumn}}>Rückruf-Zeit</th>}
                      {!isCallcenterRole && <th style={{...styles.th, ...styles.statusColumn}}>Anruf-Status</th>}
                      <th style={{...styles.th, ...styles.statusColumn}}>PV Status</th>
                      <th style={{...styles.th, ...styles.statusColumn}}>WP Status</th>
                      <th style={{...styles.th, ...styles.statusColumn}}>Projekt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, index) => (
                      <tr 
                        key={`${lead.id}-${index}`}
                        style={{
                          ...styles.row,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)',
                            borderLeft: '4px solid #4a6de5'
                          } : {})
                        }}
                        className={`${lead.organization_name === 'Energie+' ? 'lead-row-org-6' : ''}`}
                        onClick={() => handleCustomerSelect(lead.id)}
                        onMouseEnter={(e) => {
                          const target = e.target.parentElement;
                          if (lead.organization_name === 'Energie+') {
                            target.style.backgroundColor = 'rgba(74, 109, 229, 0.15)';
                          } else {
                            target.style.backgroundColor = '#f9fafb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          const target = e.target.parentElement;
                          if (lead.organization_name === 'Energie+') {
                            target.style.backgroundColor = 'rgba(74, 109, 229, 0.1)';
                          } else {
                            target.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <td style={{
                          ...styles.td, 
                          ...styles.dateColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }} title={new Date(lead.erstellt).toLocaleString('de-DE')}>
                          {new Date(lead.erstellt).toLocaleString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.productColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }}>
                          {lead.produkt ? (
                            <div style={styles.productBadges}>
                              {lead.produkt.toLowerCase().includes('pv') && (
                                <span style={{...styles.productBadge, ...styles.pvBadge}}>PV</span>
                              )}
                              {lead.produkt.toLowerCase().includes('wp') && (
                                <span style={{...styles.productBadge, ...styles.wpBadge}}>WP</span>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.statusColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }}>
                          {(lead.signature || lead.opt_in) ? (
                            <span style={{...styles.statusBadge, ...styles.statusCompleted}} title="Opt In vorhanden">
                              ✓
                            </span>
                          ) : (
                            <span style={{...styles.statusBadge, ...styles.statusNew}} title="Kein Opt In">
                              ✗
                            </span>
                          )}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.nameColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }} title={`${lead.name || ''} ${lead.nachname || ''}`.trim()}>
                          {truncateText(`${lead.name || ''} ${lead.nachname || ''}`.trim(), isMobile ? 12 : isTablet ? 15 : 20)}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.emailColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }} title={lead.email}>
                          {truncateText(lead.email, isMobile ? 15 : isTablet ? 18 : 25)}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.phoneColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }} title={lead.handy}>
                          {truncateText(lead.handy, isMobile ? 12 : 15)}
                        </td>
                        {isCallcenterRole && (
                          <td style={{
                            ...styles.td, 
                            ...styles.teamColumn,
                            ...(lead.organization_name === 'Energie+' ? {
                              backgroundColor: 'rgba(74, 109, 229, 0.1)'
                            } : {})
                          }} title={teams.find(t => t.id.toString() === lead.team_id?.toString())?.name || 'Unbekanntes Team'}>
                            {getTeamName(lead.team_id)}
                          </td>
                        )}
                        {isCallcenterRole && (
                          <td style={{
                            ...styles.td, 
                            ...styles.callsColumn,
                            ...(lead.organization_name === 'Energie+' ? {
                              backgroundColor: 'rgba(74, 109, 229, 0.1)'
                            } : {})
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <span style={{
                                ...styles.statusBadge, 
                                ...(lead.call_count > 0 ? styles.statusCompleted : styles.statusNew),
                                marginBottom: lead.last_call_status ? '2px' : '0'
                              }}>
                                {lead.call_count || 0}
                              </span>
                              {lead.last_call_status && (
                                <div style={{ 
                                  fontSize: isMobile ? '0.5rem' : '0.55rem', 
                                  color: '#666', 
                                  textAlign: 'center',
                                  lineHeight: '1.1'
                                }} title={lead.last_call_status}>
                                  {truncateText(lead.last_call_status, isMobile ? 6 : 8)}
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                        {isCallcenterRole && showCallbackTimeColumn && (
                          <td style={{
                            ...styles.td, 
                            ...styles.callsColumn,
                            ...(lead.organization_name === 'Energie+' ? {
                              backgroundColor: 'rgba(74, 109, 229, 0.1)'
                            } : {}),
                            color: lead.ruckruf_zeit ? '#4a6de5' : '#718096',
                            fontWeight: lead.ruckruf_zeit ? '500' : 'normal',
                            fontSize: isMobile ? '0.6rem' : '0.75rem'
                          }}>
                            {formatCallbackTime(lead.ruckruf_zeit)}
                          </td>
                        )}
                        {!isCallcenterRole && (
                          <td style={{
                            ...styles.td, 
                            ...styles.statusColumn,
                            ...(lead.organization_name === 'Energie+' ? {
                              backgroundColor: 'rgba(74, 109, 229, 0.1)'
                            } : {})
                          }}>
                            <span style={{
                              ...styles.statusBadge,
                              ...(lead.last_call_status ? styles.statusCompleted : styles.statusNew)
                            }} title={lead.last_call_status || 'Kein Anruf-Status'}>
                              {lead.last_call_status ? truncateText(lead.last_call_status, isMobile ? 8 : 12) : '-'}
                            </span>
                          </td>
                        )}
                        <td style={{
                          ...styles.td, 
                          ...styles.statusColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }}>
                          {renderStatus(lead.status_pv, 'pv', lead.produkt, lead.status_pv_invalid)}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.statusColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }}>
                          {renderStatus(lead.status_wp, 'wp', lead.produkt, lead.status_wp_invalid)}
                        </td>
                        <td style={{
                          ...styles.td, 
                          ...styles.statusColumn,
                          ...(lead.organization_name === 'Energie+' ? {
                            backgroundColor: 'rgba(74, 109, 229, 0.1)'
                          } : {})
                        }} title={lead.projekt}>
                          {truncateText(lead.projekt, isMobile ? 8 : isTablet ? 10 : 12)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={styles.mobileScrollHint}>
                  ← Horizontal scrollen für weitere Spalten →
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Leads;